import React, { useState, useContext, useCallback } from "react";
import {
  FaTimes,
  FaPlus,
  FaPaperclip,
  FaVideo,
  FaUsers,
  FaComments,
  FaShareAlt,
  FaSave,
  FaEye,
  FaListAlt,
  FaNotesMedical,
  FaSignOutAlt,
} from "react-icons/fa";
import { signOutCreUser } from "../../cre/creSignOut";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import BackButton from "../common/BackButton";
import { useGlobalStore } from "../../context/UserContext/userContext";
import { usePatientDetails } from "../../context/patientDetails/patientDetails";
import { toast } from "react-toastify";
import { createReports } from "../../utils/API_SERVICE";
import { sendWixReportFromPatientDetails } from "../../utils/API_SERVICE_FOR_PATIENT_PAGE";
import { usePatientAuthFlags } from "../../Hooks/usePatientAuthFlags";
import { useStudyId } from "../../Hooks/useStudyId";
import Spinner from "../Spinner";
import { useNewPatientReport } from "../../pages/patient/hooks/useNewPatientReport";
import { buildPatientTemplatesMacrosPath } from "../../utils/buildPatientAuthUrl";
import { StudyContext } from "../../context/studyData/studyData";
import { getSaveContentFromReport } from "../../utils/contentProcessor";
import { useTranslation } from "react-i18next";
import { useCrePermission } from "../../Hooks/useCrePermission";
import { CRE_PERMISSION } from "../../cre/crePermissions";

function Menubar({
  onSelect,
  showPreview,
  transcript,
  editorRef,
  language,
  chatUnreadCount = 0,
  onReportSaved,
}) {
  const { t } = useTranslation(["common", "protocol", "acr"]);
  // const [menuOpen, setMenuOpen] = useState(false);
  const [selectedButton, setSelectedButton] = useState(""); // State to track the selected button
  const { startNewPatientReport, isResetting } = useNewPatientReport();
  const [isSaving, setIsSaving] = useState(false);
  const { isWixAuth, isCreAuth, isRadicalAuth, creUser } = usePatientAuthFlags();
  const { requirePermission, can } = useCrePermission();
  const wixToken = useGlobalStore((s) => s.wixToken);
  const getAccessToken = useGlobalStore((s) => s.getAccessToken);
  const [searchParams] = useSearchParams();
  const studyId = useStudyId(searchParams.get("StudyId"));
  const { patientDetails } = usePatientDetails();
  const location = useLocation();
  const { studyData: globalStudyData } = useContext(StudyContext);

  // const toggleMenu = () => {
  //   setMenuOpen(!menuOpen);
  // };

  const resetSelectedButton = useCallback(() => {
    onSelect(""); // Deselect and show the main page
    setSelectedButton(""); // Reset the selected button
  }, [onSelect, setSelectedButton]);

  const handleButtonClick = useCallback(
    (buttonName, permissionCode) => {
      if (permissionCode && !requirePermission(permissionCode)) {
        return;
      }
      if (selectedButton === buttonName) {
        resetSelectedButton();
      } else {
        onSelect(buttonName); // Call the onSelect function passed from the parent
        setSelectedButton(buttonName); // Update the selected button
      }
    },
    [selectedButton, resetSelectedButton, onSelect, requirePermission],
  );

  // Reusable function to check if a field is missing in both data sources
  const isFieldMissing = useCallback((patientValue, studyValue) => {
    const patientMissing = !patientValue || patientValue.trim() === "";
    const studyMissing = !studyValue || studyValue.trim() === "";
    return patientMissing || studyMissing;
  }, []);

  // Validation function for required patient details
  const validatePatientDetails = useCallback(() => {
    const missingFields = [];

    // Check Patient Name
    if (
      isFieldMissing(patientDetails.patientName, globalStudyData?.patientName)
    ) {
      missingFields.push("Patient Name");
    }

    // Check Date of Birth
    if (
      isFieldMissing(patientDetails.dateOfBirth, globalStudyData?.dateOfBirth)
    ) {
      missingFields.push("Patient DOB");
    }

    // Check Patient Sex
    if (
      isFieldMissing(patientDetails.patientSex, globalStudyData?.patientSex)
    ) {
      missingFields.push("Patient Gender");
    }

    // Check Patient ID
    if (isFieldMissing(patientDetails.patientId, globalStudyData?.patientId)) {
      missingFields.push("Patient ID");
    }

    if (missingFields.length > 0) {
      const fieldList = missingFields.join(", ");
      toast.error(`Please fill in the following required fields: ${fieldList}`);
      return false;
    }

    return true;
  }, [patientDetails, globalStudyData, isFieldMissing]);

  const handleSaveReport = async () => {
    if (!requirePermission(CRE_PERMISSION.BASIC)) {
      return;
    }
    // Validate patient details before proceeding
    if (!validatePatientDetails()) {
      return;
    }

    setIsSaving(true);
    try {
      if (isCreAuth) {
        const token = await getAccessToken();
        if (!token || !studyId) {
          toast.error(t("failedToSaveReport", { defaultValue: "Failed to save report" }));
          return;
        }
        await createReports({
          StudyId: studyId,
          RelatedUserId: creUser?.userId || null,
          ReportContent: getSaveContentFromReport({
            editor: editorRef?.current?.editor,
            transcript,
          }),
        });
        onReportSaved?.();
        return;
      }

      if (!isWixAuth) {
        return;
      }

      if (!wixToken) {
        toast.error("Missing Wix token");
        return;
      }

      toast.info("Generating PDF and sending report...");

      const data = await sendWixReportFromPatientDetails(
        getSaveContentFromReport({
          editor: editorRef?.current?.editor,
          transcript,
        }),
        globalStudyData,
        language,
      );
      if (data?.success) {
        toast.success("Report sent successfully");
      } else {
        toast.success("Report sent");
      }
      onReportSaved?.();
    } catch (error) {
      console.error("Error sending Wix report:", error);
      toast.error("Failed to send report");
    } finally {
      setIsSaving(false);
    }
  };

  const handlePreview = () => {
    if (!requirePermission(CRE_PERMISSION.BASIC)) {
      return;
    }
    // Validate patient details before showing preview
    if (!validatePatientDetails()) {
      return;
    }

    showPreview();
  };

  const handleReset = () => {
    if (!requirePermission(CRE_PERMISSION.BASIC)) {
      return;
    }
    void startNewPatientReport();
  };

  const handleTemplatesAndMacrosClick = (event) => {
    if (!requirePermission(CRE_PERMISSION.BASIC)) {
      event.preventDefault();
    }
  };

  if (isRadicalAuth) {
    return null;
  }

  return (
    <div className="flex flex-col gap-3 mt-6">
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
        <Link
          to={buildPatientTemplatesMacrosPath(location.search)}
          className="flex-shrink-0"
          onClick={handleTemplatesAndMacrosClick}
        >
          <button
            type="button"
            className="bg-gray-200 dark:bg-gray-700 flex flex-row justify-center items-center gap-2 h-10 rounded-lg xl:rounded-full px-3 xl:hidden hover:bg-gray-300 dark:hover:bg-gray-600 transition-all duration-200 shadow-sm hover:shadow-md"
          >
            <FaPlus
              size={14}
              className="text-gray-700 dark:text-gray-300"
            />
            <span className="text-gray-800 dark:text-gray-200 font-semibold text-sm tracking-wide">
              T & M
            </span>
          </button>
          <div className="hidden xl:block">
            <BackButton text={t("createTemplatesAndMacros")} />
          </div>
        </Link>
        <div className="flex space-x-2 sm:space-x-3 font-poppins overflow-x-auto flex-1 min-w-0 scrollbar-hide mobile-scroll-x">
          {/* {!isWixAuth && (
            <button
              onClick={() => handleButtonClick("Study Details")}
              title={t('studyDetails')}
              className={`border dark:border-gray-700 dark:text-white h-11 sm:h-10 rounded-lg xl:rounded-full px-2 xl:px-3 flex items-center justify-center flex-shrink-0 min-w-[44px] sm:min-w-[44px] ${
                selectedButton === "Study Details"
                  ? "bg-[#CBEA7B] dark:bg-[#A4C63980]"
                  : "hover:bg-[#A4C639] dark:hover:bg-[#CBEA7B] dark:hover:text-black"
              }`}
            >
              <FaFileAlt className="block 2xl:hidden" size={14} />
              <span className="hidden 2xl:block">{t('studyDetails')}</span>
            </button>
          )} */}
          <button
            onClick={() =>
              handleButtonClick("Attached Documents", CRE_PERMISSION.IMAGE_AI)
            }
            title={t("attachedDocuments")}
            className={`border dark:border-gray-700 dark:text-white h-11 sm:h-10 rounded-lg xl:rounded-full px-2 xl:px-3 flex items-center justify-center flex-shrink-0 min-w-[44px] sm:min-w-[44px] ${
              selectedButton === "Attached Documents"
                ? "bg-[#CBEA7B] dark:bg-[#A4C63980]"
                : "hover:bg-[#A4C639] dark:hover:bg-[#CBEA7B] dark:hover:text-black"
            }`}
          >
            <FaPaperclip className="block 2xl:hidden" size={14} />
            <span className="hidden 2xl:block">{t("attachedDocuments")}</span>
          </button>
          <button
            onClick={() =>
              handleButtonClick(
                "Video Recording",
                CRE_PERMISSION.VIDEO_RECORDING,
              )
            }
            title={t("videoRecording")}
            className={`border dark:border-gray-700 dark:text-white h-11 sm:h-10 rounded-lg xl:rounded-full px-2 xl:px-3 flex items-center justify-center flex-shrink-0 min-w-[44px] sm:min-w-[44px] ${
              selectedButton === "Video Recording"
                ? "bg-[#CBEA7B] dark:bg-[#A4C63980]"
                : "dark:bg-gray-900 hover:bg-[#A4C639] dark:hover:bg-[#CBEA7B] dark:hover:text-black"
            }`}
          >
            <FaVideo className="block 2xl:hidden" size={14} />
            <span className="hidden 2xl:block">{t("videoRecording")}</span>
          </button>
          {(isWixAuth || isCreAuth) && (
            <>
              <button
                onClick={() =>
                  handleButtonClick("MDT Recording", CRE_PERMISSION.MDT)
                }
                title={t("mdt.recording")}
                className={`border dark:border-gray-700 dark:text-white h-11 sm:h-10 rounded-lg xl:rounded-full px-2 xl:px-3 flex items-center justify-center flex-shrink-0 min-w-[44px] sm:min-w-[44px] ${
                  selectedButton === "MDT Recording"
                    ? "bg-[#CBEA7B] dark:bg-[#A4C63980]"
                    : "dark:bg-gray-900 hover:bg-[#A4C639] dark:hover:bg-[#CBEA7B] dark:hover:text-black"
                }`}
              >
                <FaUsers className="block 2xl:hidden" size={14} />
                <span className="hidden 2xl:block">{t("mdt.recording")}</span>
              </button>

              <button
                onClick={() =>
                  handleButtonClick("Protocol", CRE_PERMISSION.PROTOCOLS)
                }
                title={t("protocol:title")}
                className={`relative border dark:border-gray-700 dark:text-white h-11 sm:h-10 rounded-lg xl:rounded-full px-2 xl:px-3 flex items-center justify-center flex-shrink-0 min-w-[44px] sm:min-w-[44px] ${
                  selectedButton === "Protocol"
                    ? "bg-[#CBEA7B] dark:bg-[#A4C63980]"
                    : "dark:bg-gray-900 hover:bg-[#A4C639] dark:hover:bg-[#CBEA7B] dark:hover:text-black"
                }`}
              >
                <FaListAlt className="block 2xl:hidden" size={14} />
                <span className="hidden 2xl:block">{t("protocol:title")}</span>
              </button>

              <button
                onClick={() => handleButtonClick("ACR", CRE_PERMISSION.ACR)}
                title={t("acr:toolbarButton")}
                className={`border dark:border-gray-700 dark:text-white h-11 sm:h-10 rounded-lg xl:rounded-full px-2 xl:px-3 flex items-center justify-center flex-shrink-0 min-w-[44px] sm:min-w-[44px] ${
                  selectedButton === "ACR"
                    ? "bg-[#CBEA7B] dark:bg-[#A4C63980]"
                    : "dark:bg-gray-900 hover:bg-[#A4C639] dark:hover:bg-[#CBEA7B] dark:hover:text-black"
                }`}
              >
                <FaNotesMedical className="block 2xl:hidden" size={14} />
                <span className="hidden 2xl:block">{t("acr:toolbarButton")}</span>
              </button>
            </>
          )}
          {isCreAuth && can(CRE_PERMISSION.SHARE_FILE) && (
            <button
              onClick={() =>
                handleButtonClick("PACSShare", CRE_PERMISSION.SHARE_FILE)
              }
              title={t("pacsShareTab")}
              className={`border dark:border-gray-700 dark:text-white h-11 sm:h-10 rounded-lg xl:rounded-full px-2 xl:px-3 flex items-center justify-center flex-shrink-0 min-w-[44px] sm:min-w-[44px] ${
                selectedButton === "PACSShare"
                  ? "bg-[#CBEA7B] dark:bg-[#A4C63980]"
                  : "dark:bg-gray-900 hover:bg-[#A4C639] dark:hover:bg-[#CBEA7B] dark:hover:text-black"
              }`}
            >
              <FaShareAlt className="block 2xl:hidden" size={14} />
              <span className="hidden 2xl:block">{t("pacsShareTab")}</span>
            </button>
          )}
          <button
            onClick={() => handleButtonClick("Chats", CRE_PERMISSION.CHAT)}
            title={t("chat")}
            className={`relative border dark:border-gray-700 dark:text-white h-11 sm:h-10 rounded-lg xl:rounded-full px-2 xl:px-3 flex items-center justify-center flex-shrink-0 min-w-[44px] sm:min-w-[44px] ${
              selectedButton === "Chats"
                ? "bg-[#CBEA7B] dark:bg-[#A4C63980]"
                : "dark:bg-gray-900 hover:bg-[#A4C639] dark:hover:bg-[#CBEA7B] dark:hover:text-black"
            }`}
          >
            <FaComments className="block 2xl:hidden" size={14} />
            <span className="hidden 2xl:block">{t("chat")}</span>
            {chatUnreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-red-500 text-white text-xs font-medium">
                {chatUnreadCount > 99 ? "99+" : chatUnreadCount}
              </span>
            )}
          </button>
          {/* {!isWixAuth && (
            <>
              <button
                onClick={() => handleButtonClick("Critical Findings")}
                title={t('criticalFindings')}
                className={`border dark:border-gray-700 dark:text-white h-11 sm:h-10 rounded-lg xl:rounded-full px-2 xl:px-3 flex items-center justify-center flex-shrink-0 min-w-[44px] sm:min-w-[44px] ${
                  selectedButton === "Critical Findings"
                    ? "bg-[#CBEA7B] dark:bg-[#A4C63980]"
                    : "dark:bg-gray-900 hover:bg-[#A4C639] dark:hover:bg-[#CBEA7B] dark:hover:text-black"
                }`}
              >
                <FaExclamationTriangle className="block xl:hidden" size={14} />
                <span className="hidden xl:block">{t('criticalFindings')}</span>
              </button>
              <button
                onClick={() => handleButtonClick("Wet Reads")}
                title={t('wetReads')}
                className={`border dark:border-gray-700 dark:text-white h-11 sm:h-10 rounded-lg xl:rounded-full px-2 xl:px-3 flex items-center justify-center flex-shrink-0 min-w-[44px] sm:min-w-[44px] ${
                  selectedButton === "Wet Reads"
                    ? "bg-[#CBEA7B] dark:bg-[#A4C63980]"
                    : "dark:bg-gray-900 hover:bg-[#A4C639] dark:hover:bg-[#CBEA7B] dark:hover:text-black"
                }`}
              >
                <FaBook className="block xl:hidden" size={14} />
                <span className="hidden xl:block">{t('wetReads')}</span>
              </button>
            </>
          )} */}
        </div>
        {/* Desktop Top Action Buttons */}
        {(isWixAuth || isCreAuth) && (
          <div className="flex gap-2 sm:gap-3 overflow-x-auto flex-shrink-0 scrollbar-hide mobile-scroll-x">
            <button
              title={t("saveReport")}
              className={`border h-11 sm:h-10 rounded-lg xl:rounded-full px-3 sm:px-2 xl:px-3 xl:min-w-28 font-medium text-gray-800 bg-[#CBEA7B80] dark:border-gray-700 hover:bg-[#A4C639] dark:hover:bg-[#CBEA7B] dark:hover:text-black transition-colors cursor-pointer flex items-center justify-center gap-2 flex-shrink-0 min-w-[44px] sm:min-w-[44px] ${
                isSaving ? " opacity-60 cursor-not-allowed" : ""
              }`}
              onClick={handleSaveReport}
              disabled={isSaving}
            >
              {isSaving && <Spinner size={14} color="currentColor" />}
              <FaSave className="block xl:hidden" size={14} />
              <span className="hidden xl:block">{t("saveReport")}</span>
              <span className="hidden lg:block xl:hidden">{t("save")}</span>
            </button>
            <button
              title={t("previewAndDownload")}
              className="border h-11 sm:h-10 rounded-lg xl:rounded-full px-3 sm:px-2 xl:px-3 xl:min-w-28 font-medium bg-[#CBEA7B80] text-gray-800 dark:border-gray-700 hover:bg-[#A4C639] dark:hover:bg-[#CBEA7B] dark:hover:text-black transition-colors cursor-pointer flex items-center justify-center flex-shrink-0 min-w-[44px] sm:min-w-[44px]"
              onClick={handlePreview}
            >
              <FaEye className="block xl:hidden" size={14} />
              <span className="hidden xl:block">{t("previewAndDownload")}</span>
            </button>
            {isWixAuth && (
              <button
                title={t("close")}
                className="border h-11 sm:h-10 rounded-lg xl:rounded-full px-3 sm:px-2 xl:px-3 xl:min-w-28 font-medium text-gray-800 bg-[#CBEA7B80] dark:border-gray-700 hover:bg-[#A4C639] dark:hover:bg-[#CBEA7B] dark:hover:text-black transition-colors cursor-pointer flex items-center justify-center gap-2 flex-shrink-0 min-w-[44px] sm:min-w-[44px]"
                onClick={() => window.open("https://medilis.ai", "_blank")}
              >
                <FaTimes className="block xl:hidden" size={14} />
                <span>{t("close")}</span>
              </button>
            )}
            {isCreAuth && (
              <button
                type="button"
                title={t("signOut", { defaultValue: "Sign out" })}
                className="border h-11 sm:h-10 rounded-lg xl:rounded-full px-3 sm:px-2 xl:px-3 xl:min-w-28 font-medium text-gray-800 bg-[#CBEA7B80] dark:border-gray-700 hover:bg-[#A4C639] dark:hover:bg-[#CBEA7B] dark:hover:text-black transition-colors cursor-pointer flex items-center justify-center gap-2 flex-shrink-0 min-w-[44px] sm:min-w-[44px]"
                onClick={() => signOutCreUser()}
              >
                <FaSignOutAlt className="block xl:hidden" size={14} />
                <span>{t("signOut", { defaultValue: "Sign out" })}</span>
              </button>
            )}

            <button
              title={t("newPatientReport")}
              className={`border h-11 sm:h-10 rounded-lg xl:rounded-full px-3 sm:px-2 xl:px-3 xl:min-w-28 font-medium text-gray-800 bg-[#CBEA7B80] dark:border-gray-700 hover:bg-[#A4C639] dark:hover:bg-[#CBEA7B] dark:hover:text-black transition-colors cursor-pointer flex items-center justify-center gap-2 flex-shrink-0 min-w-[44px] sm:min-w-[44px] ${
                isResetting ? " opacity-60 cursor-not-allowed" : ""
              }`}
              onClick={handleReset}
              disabled={isResetting}
            >
              <FaPlus className="w-3 h-3 sm:w-4 sm:h-4" />
              {isResetting && <Spinner size={14} color="currentColor" />}
              <span className="hidden xl:block">{t("newPatientReport")}</span>
              <span className="block xl:hidden">{t("new")}</span>
            </button>
          </div>
        )}
      </div>
      {/* Mobile Top Action Buttons */}
      {/* {isWixAuth && (
        <div className="flex gap-3 flex-col md:flex-row xl:hidden">
          <button
            className={`border h-10 rounded-full px-3 lg:min-w-28 font-medium text-gray-800 bg-[#CBEA7B80] dark:border-gray-700   hover:bg-[#A4C639] dark:hover:bg-[#CBEA7B] dark:hover:text-black transition-colors cursor-pointer flex items-center justify-center gap-2${
              isSaving ? " opacity-60 cursor-not-allowed" : ""
            }`}
            onClick={handleSaveReport}
            disabled={isSaving}
          >
            {isSaving && <Spinner size={14} color="currentColor" />}
            Save Report
          </button>
          <button
            className="border h-10 rounded-full px-3 lg:min-w-28 font-medium bg-[#CBEA7B80] text-gray-800 dark:border-gray-700   hover:bg-[#A4C639] dark:hover:bg-[#CBEA7B] dark:hover:text-black transition-colors cursor-pointer"
            onClick={handlePreview}
          >
            Preview & Download
          </button>
          <button
            className="border h-10 rounded-full px-3 lg:min-w-28 font-medium text-gray-800 bg-[#CBEA7B80] dark:border-gray-700   hover:bg-[#A4C639] dark:hover:bg-[#CBEA7B] dark:hover:text-black transition-colors cursor-pointer"
            onClick={() => window.open("https://medilis.ai", "_blank")}
          >
            Close
          </button>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              className={`border h-10 rounded-full px-3 lg:min-w-28 font-medium text-gray-800 bg-[#CBEA7B80] dark:border-gray-700   hover:bg-[#A4C639] dark:hover:bg-[#CBEA7B] dark:hover:text-black transition-colors cursor-pointer flex items-center justify-center gap-2${
                isResetting ? " opacity-60 cursor-not-allowed" : ""
              }`}
              onClick={handleReset}
              disabled={isResetting}
            >
              <FaPlus className="w-3 h-3" />
              {isResetting && <Spinner size={14} color="currentColor" />}
              New Patient Report
            </button>
            {selectedButton && (
              <div className="flex items-center gap-2 bg-[#CBEA7B] dark:bg-[#A4C63980] border border-gray-300 dark:border-gray-700 rounded-full px-3 py-2 h-10">
                <span className="text-sm font-medium text-gray-800 dark:text-black whitespace-nowrap">
                  {selectedButton}
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    resetSelectedButton();
                  }}
                  className="flex items-center justify-center w-5 h-5 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  aria-label="Close tab"
                >
                  <FaTimes className="w-3 h-3 text-gray-800 dark:text-black" />
                </button>
              </div>
            )}
          </div>
        </div>
      )} */}
    </div>
  );
}

export default Menubar;
